import axios from "api/axios";
import { IconButton, OutlineButton } from "components/button";
import {
  ChecklistExistingPicker,
  type TChecklistPickerRow,
} from "components/checklist_dropdown";
import { Input } from "components/input";
import {
  deleteAppointmentChecklistEntry,
  patchAppointmentChecklistEntry,
  postAppointmentChecklistEntry,
} from "features/requests/api/appointmentChecklistEntries";
import { getAppointmentChecklists } from "features/requests/api/getAppointmentChecklists";
import type { IChecklistWithResponses } from "features/requests/types/checklists";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { CgUndo } from "react-icons/cg";
import { RiArrowDownSLine, RiArrowRightSLine, RiCloseCircleFill, RiDeleteBin2Fill } from "react-icons/ri";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";

type PendingAdd = {
  tempKey: string;
  fk_checklist_id: number;
  label: string;
  required: boolean | null;
};

type TChecklistTemplateRow = {
  id: number;
  fk_appointment_type_id: number | null;
  fk_sub_appointment_type_id: number | null;
  label: string | null;
  order: number | null;
  required: boolean | null;
};

export type AppointmentModalChecklistsSectionHandle = {
  flushPending: (apptId: number) => Promise<void>;
};

type Props = {
  appointmentId?: number | null;
  fkAppointmentTypeId: string;
  fkAppointmentTypeDefaultId: string | null;
  selectedDefaultMode?: string | null;
  /** True when the modal shows the sub-type / default Field (type has active appointment_type_defaults). */
  hasSubAppointmentDefaultPicker?: boolean;
  isCompleted: boolean;
  onDisplayCountChange?: (count: number) => void;
};

const parseDefaultId = (raw: string | null): number | null => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const templateMatchesAppointmentScope = (
  row: TChecklistTemplateRow,
  typeId: number,
  defaultId: number | null
) => {
  if (Number(row.fk_appointment_type_id) !== typeId) return false;
  if (defaultId == null) {
    return row.fk_sub_appointment_type_id == null;
  }
  return Number(row.fk_sub_appointment_type_id) === defaultId;
};

export const AppointmentModalChecklistsSection = forwardRef<
  AppointmentModalChecklistsSectionHandle,
  Props
>(({
  appointmentId = null,
  fkAppointmentTypeId,
  fkAppointmentTypeDefaultId,
  selectedDefaultMode = null,
  hasSubAppointmentDefaultPicker = true,
  isCompleted,
  onDisplayCountChange,
}, ref) => {
  const { t } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const tr = React.useCallback(
    (key: string, options?: Record<string, string | number>) =>
      t(`${tBase}.${key}`, options),
    [t]
  );
  const queryClient = useQueryClient();

  const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([]);
  const [checklistRowsExpanded, setChecklistRowsExpanded] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const templatePickerAnchorRef = useRef<HTMLDivElement | null>(null);
  const [hiddenTemplateIds, setHiddenTemplateIds] = useState<number[]>([]);
  const [pendingDeleteEntryIds, setPendingDeleteEntryIds] = useState<number[]>([]);
  const [pendingPatchesByEntryId, setPendingPatchesByEntryId] = useState<
    Record<number, { label: string; required: boolean | null }>
  >({});

  const pendingSnapRef = useRef({
    adds: [] as PendingAdd[],
    deletes: [] as number[],
    patches: {} as Record<number, { label: string; required: boolean | null }>,
  });
  pendingSnapRef.current = {
    adds: pendingAdds,
    deletes: pendingDeleteEntryIds,
    patches: pendingPatchesByEntryId,
  };

  const appointmentIdNum =
    appointmentId != null && Number.isFinite(Number(appointmentId))
      ? Number(appointmentId)
      : null;

  const checklistQuery = useQuery(
    ["appointment-checklists", appointmentIdNum ?? "new"],
    () => getAppointmentChecklists(appointmentIdNum as number),
    { enabled: appointmentIdNum != null, refetchOnWindowFocus: false }
  );

  const typeNum = Number(fkAppointmentTypeId);
  const defaultNum = parseDefaultId(fkAppointmentTypeDefaultId);

  // Parent-only types (no sub appointment defaults): there is no second dropdown, so "new" is not an explicit
  // "(No auto-fill)" choice — still auto-offer parent-scoped checklist templates like other auto modes.
  const allowChecklistAutoOffer =
    selectedDefaultMode !== "new" || !hasSubAppointmentDefaultPicker;

  const templatesQuery = useQuery(
    ["eserv-business-checklists", "appointment-modal-scope", typeNum, defaultNum ?? "parent"],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/checklist`
      );
      const raw = Array.isArray(res.data) ? res.data : [];
      return raw.map((row: TChecklistTemplateRow) => ({
        ...row,
        required: row.required === true ? true : row.required === false ? false : null,
      })) as TChecklistTemplateRow[];
    },
    { enabled: Number.isFinite(typeNum), refetchOnWindowFocus: false }
  );

  /** Templates for current type/default scope not yet on the appointment (always computed; auto-import uses a subset). */
  const scopedTemplatesNotYetAdded = useMemo(() => {
    if (!Number.isFinite(typeNum)) return [];
    const rows = templatesQuery.data ?? [];
    const scoped = rows.filter((r) => templateMatchesAppointmentScope(r, typeNum, defaultNum));
    const blocked = new Set<number>();
    for (const r of checklistQuery.data ?? []) {
      if (r.entry_id != null) blocked.add(r.id);
    }
    for (const p of pendingAdds) {
      blocked.add(p.fk_checklist_id);
    }
    return scoped.filter((r) => r.id != null && !blocked.has(r.id));
  }, [typeNum, defaultNum, templatesQuery.data, checklistQuery.data, pendingAdds]);

  const offerTemplates = useMemo(() => {
    if (!allowChecklistAutoOffer) return [];
    return scopedTemplatesNotYetAdded;
  }, [allowChecklistAutoOffer, scopedTemplatesNotYetAdded]);

  const autoImportScopeKey = useMemo(
    () =>
      [
        appointmentIdNum ?? "new",
        Number.isFinite(typeNum) ? String(typeNum) : "none",
        defaultNum == null ? "parent" : String(defaultNum),
        selectedDefaultMode ?? "none",
        hasSubAppointmentDefaultPicker ? "subpick" : "nosubpick",
      ].join("|"),
    [appointmentIdNum, typeNum, defaultNum, selectedDefaultMode, hasSubAppointmentDefaultPicker]
  );
  const importedScopeRef = useRef<string | null>(null);

  // For existing appointments, do not overwrite checklists on initial modal load.
  // We only want overwrite behavior after the user changes type/default.
  useEffect(() => {
    if (appointmentIdNum == null) return;
    if (!checklistQuery.isSuccess) return;
    if (importedScopeRef.current != null) return;
    importedScopeRef.current = autoImportScopeKey;
  }, [appointmentIdNum, checklistQuery.isSuccess, autoImportScopeKey]);

  const displayRows = useMemo(() => {
    const deleteSet = new Set(pendingDeleteEntryIds);
    const hiddenTemplateIdSet = new Set(hiddenTemplateIds);
    const pendingTemplateIdSet = new Set(pendingAdds.map((p) => p.fk_checklist_id));
    const base = (checklistQuery.data ?? [])
      .filter((row) => {
        if (hiddenTemplateIdSet.has(row.id)) return false;
        if (pendingTemplateIdSet.has(row.id)) return false;
        if (row.entry_id == null) return true;
        return !deleteSet.has(row.entry_id);
      })
      .map((row) => {
        if (row.entry_id != null && pendingPatchesByEntryId[row.entry_id]) {
          return {
            ...row,
            ...pendingPatchesByEntryId[row.entry_id],
          } as IChecklistWithResponses;
        }
        return row;
      });
    const virtuals: IChecklistWithResponses[] = pendingAdds.map((p) => ({
      id: p.fk_checklist_id,
      fk_appointment_type_id: typeNum,
      label: p.label,
      order: null,
      fk_company_id: null,
      type: null,
      required: p.required,
      is_done: false,
      entry_id: null,
      __pendingTempKey: p.tempKey,
    }));
    return [...base, ...virtuals];
  }, [
    checklistQuery.data,
    hiddenTemplateIds,
    pendingDeleteEntryIds,
    pendingPatchesByEntryId,
    pendingAdds,
    typeNum,
  ]);

  useEffect(() => {
    onDisplayCountChange?.(displayRows.length);
  }, [displayRows.length, onDisplayCountChange]);

  const resetPending = () => {
    setPendingAdds([]);
    setHiddenTemplateIds([]);
    setPendingDeleteEntryIds([]);
    setPendingPatchesByEntryId({});
  };

  const flushPending = async (apptId: number) => {
    const p = pendingSnapRef.current;
    const has =
      p.adds.length > 0 || p.deletes.length > 0 || Object.keys(p.patches).length > 0;
    if (!has) return;
    for (const entryId of p.deletes) {
      await deleteAppointmentChecklistEntry(apptId, entryId);
    }
    for (const [entryIdStr, patch] of Object.entries(p.patches)) {
      const entryId = Number(entryIdStr);
      if (p.deletes.includes(entryId)) continue;
      await patchAppointmentChecklistEntry(apptId, entryId, {
        label: patch.label,
        required: patch.required,
      });
    }
    for (const add of p.adds) {
      await postAppointmentChecklistEntry(apptId, add.fk_checklist_id);
    }
    resetPending();
    await queryClient.invalidateQueries(["appointment-checklists", apptId]);
  };

  const flushPendingRef = useRef(flushPending);
  flushPendingRef.current = flushPending;

  useImperativeHandle(ref, () => ({
    flushPending: (apptId: number) => flushPendingRef.current(apptId),
  }));

  useEffect(() => {
    resetPending();
    setChecklistRowsExpanded(false);
  }, [appointmentIdNum]);

  const handleLabelChange = (row: IChecklistWithResponses, nextLabel: string) => {
    const pendingKey = (row as IChecklistWithResponses & { __pendingTempKey?: string })
      .__pendingTempKey;
    if (pendingKey) {
      setPendingAdds((prev) =>
        prev.map((p) => (p.tempKey === pendingKey ? { ...p, label: nextLabel } : p))
      );
      return;
    }
    if (row.entry_id == null) {
      setHiddenTemplateIds((prev) =>
        prev.includes(row.id) ? prev : [...prev, row.id]
      );
      setPendingAdds((prev) => {
        const existing = prev.find((p) => p.fk_checklist_id === row.id);
        if (existing) {
          return prev.map((p) =>
            p.fk_checklist_id === row.id ? { ...p, label: nextLabel } : p
          );
        }
        return [
          ...prev,
          {
            tempKey: `p-${Date.now()}-${prev.length}`,
            fk_checklist_id: row.id,
            label: nextLabel,
            required: row.required ?? null,
          },
        ];
      });
      return;
    }
    setPendingPatchesByEntryId((prev) => ({
      ...prev,
      [row.entry_id!]: {
        label: nextLabel,
        required:
          prev[row.entry_id!]?.required !== undefined
            ? prev[row.entry_id!]!.required
            : row.required ?? null,
      },
    }));
  };

  const handleRequiredChange = (row: IChecklistWithResponses, checked: boolean) => {
    const pendingKey = (row as IChecklistWithResponses & { __pendingTempKey?: string })
      .__pendingTempKey;
    if (pendingKey) {
      setPendingAdds((prev) =>
        prev.map((p) =>
          p.tempKey === pendingKey ? { ...p, required: checked ? true : false } : p
        )
      );
      return;
    }
    if (row.entry_id == null) {
      setHiddenTemplateIds((prev) =>
        prev.includes(row.id) ? prev : [...prev, row.id]
      );
      setPendingAdds((prev) => {
        const existing = prev.find((p) => p.fk_checklist_id === row.id);
        if (existing) {
          return prev.map((p) =>
            p.fk_checklist_id === row.id
              ? { ...p, required: checked ? true : false }
              : p
          );
        }
        return [
          ...prev,
          {
            tempKey: `p-${Date.now()}-${prev.length}`,
            fk_checklist_id: row.id,
            label: row.label?.trim() || tr("Checklist untitled"),
            required: checked ? true : false,
          },
        ];
      });
      return;
    }
    setPendingPatchesByEntryId((prev) => ({
      ...prev,
      [row.entry_id!]: {
        label: prev[row.entry_id!]?.label ?? row.label ?? "",
        required: checked ? true : false,
      },
    }));
  };

  const handleDeleteRow = (row: IChecklistWithResponses) => {
    const pendingKey = (row as IChecklistWithResponses & { __pendingTempKey?: string })
      .__pendingTempKey;
    if (pendingKey) {
      setPendingAdds((prev) => prev.filter((p) => p.tempKey !== pendingKey));
      return;
    }
    if (row.entry_id == null) {
      setHiddenTemplateIds((prev) =>
        prev.includes(row.id) ? prev : [...prev, row.id]
      );
      return;
    }
    setPendingDeleteEntryIds((prev) => [...prev, row.entry_id!]);
    setPendingPatchesByEntryId((prev) => {
      const next = { ...prev };
      delete next[row.entry_id!];
      return next;
    });
  };

  const handleCancelPatchesForEntry = (entryId: number) => {
    setPendingPatchesByEntryId((prev) => {
      const next = { ...prev };
      delete next[entryId];
      return next;
    });
  };

  const handleConfirmTemplatesFromPicker = (rows: TChecklistPickerRow[]) => {
    if (!rows.length) return;
    setPendingAdds((prev) => {
      const next = [...prev];
      let i = next.length;
      for (const tmpl of rows) {
        const label = tmpl.label?.trim() || tr("Checklist untitled");
        next.push({
          tempKey: `p-${Date.now()}-${i}`,
          fk_checklist_id: tmpl.id,
          label,
          required: tmpl.required === true ? true : tmpl.required === false ? false : null,
        });
        i += 1;
      }
      return next;
    });
  };

  useEffect(() => {
    if (!Number.isFinite(typeNum) || !allowChecklistAutoOffer) {
      importedScopeRef.current = null;
      return;
    }
    if (templatesQuery.isLoading) {
      return;
    }
    if (importedScopeRef.current === autoImportScopeKey) return;

    // Overwrite current checklist view with scoped defaults for the newly selected type/default.
    // Skipped when user explicitly chose (No auto-fill) while a sub-type picker exists (allowChecklistAutoOffer false).
    // Existing persisted rows must be queued for deletion so Submit truly replaces checklist set.
    const existingEntryIds = (checklistQuery.data ?? [])
      .map((row) => row.entry_id)
      .filter((id): id is number => id != null);
    setPendingDeleteEntryIds(existingEntryIds);
    setPendingPatchesByEntryId({});
    setHiddenTemplateIds((checklistQuery.data ?? []).map((row) => row.id));
    setPendingAdds(
      offerTemplates.map((tmpl, index) => ({
        tempKey: `p-${Date.now()}-${index}`,
        fk_checklist_id: tmpl.id,
        label: tmpl.label?.trim() || tr("Checklist untitled"),
        required: tmpl.required === true ? true : tmpl.required === false ? false : null,
      }))
    );

    importedScopeRef.current = autoImportScopeKey;
  }, [
    autoImportScopeKey,
    allowChecklistAutoOffer,
    checklistQuery.data,
    offerTemplates,
    templatesQuery.isLoading,
    tr,
    typeNum,
  ]);

  const readOnly = isCompleted || (appointmentIdNum != null && checklistQuery.isLoading);

  return (
    <div className="mt-1 mb-2 px-1">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h2 className="text-lg font-semibold m-0">{tr("Checklists on appointment")}</h2>
      </div>
      {readOnly && isCompleted ? (
        <p className="text-sm text-gray-600 mb-2">{tr("Checklists read-only completed")}</p>
      ) : null}
      {!Number.isFinite(typeNum) || fkAppointmentTypeId === "" ? (
        <p className="text-sm text-gray-600">{tr("Checklists pick type hint")}</p>
      ) : null}
      {!readOnly &&
      hasSubAppointmentDefaultPicker &&
      selectedDefaultMode === "new" &&
      Number.isFinite(typeNum) &&
      scopedTemplatesNotYetAdded.length > 0 &&
      !templatesQuery.isLoading ? (
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-2 py-2 mb-2">
          {tr("Checklists no autofill templates hint", {
            count: scopedTemplatesNotYetAdded.length,
          })}
        </p>
      ) : null}
      {checklistQuery.isError ? (
        <p className="text-sm text-red-600">{tr("Checklists load error")}</p>
      ) : null}
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-2 py-2 text-left text-sm font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 border-b border-gray-200"
          onClick={() => setChecklistRowsExpanded((v) => !v)}
          aria-expanded={checklistRowsExpanded}
        >
          <span className="flex min-w-0 items-center gap-2">
            {checklistRowsExpanded ? (
              <RiArrowDownSLine className="shrink-0" size={18} aria-hidden />
            ) : (
              <RiArrowRightSLine className="shrink-0" size={18} aria-hidden />
            )}
            <span className="truncate">
              {tr("Checklists rows panel")}
              {!checklistQuery.isLoading ? ` (${displayRows.length})` : ""}
            </span>
          </span>
        </button>
        {checklistRowsExpanded ? (
          <div className="flex flex-col gap-2 p-2">
            {displayRows.length === 0 && !checklistQuery.isLoading ? (
              <p className="text-sm text-gray-500">{tr("Checklists none")}</p>
            ) : null}
            {displayRows.map((row) => {
              const pendingKey = (row as IChecklistWithResponses & { __pendingTempKey?: string })
                .__pendingTempKey;
              const rowKey = pendingKey ?? `e-${row.entry_id ?? "t"}-${row.id}`;
              const canEditFields = !readOnly;
              const hasPatch =
                row.entry_id != null && pendingPatchesByEntryId[row.entry_id] !== undefined;
              const showDismissPending = Boolean(pendingKey);
              const showUndoPatch = Boolean(row.entry_id != null && hasPatch);
              const showDeleteEntry = !pendingKey && !showUndoPatch;

              return (
                <div
                  key={rowKey}
                  className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-2 last:border-0"
                >
                  <Input
                    value={row.label ?? ""}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                      handleLabelChange(row, ev.target.value)
                    }
                    disabled={!canEditFields}
                    style={{ flex: "1 1 200px", minWidth: 0 }}
                  />
                  <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={row.required === true}
                      onChange={(ev) => handleRequiredChange(row, ev.target.checked)}
                      disabled={!canEditFields}
                    />
                    {tr("Checklists required")}
                  </label>
                  {!readOnly && (showDismissPending || showUndoPatch || showDeleteEntry) ? (
                    <IconButton
                      type="button"
                      onClick={() => {
                        if (showDismissPending) {
                          handleDeleteRow(row);
                          return;
                        }
                        if (showUndoPatch && row.entry_id != null) {
                          handleCancelPatchesForEntry(row.entry_id);
                          return;
                        }
                        if (showDeleteEntry) {
                          handleDeleteRow(row);
                        }
                      }}
                      aria-label={
                        showDismissPending
                          ? tr("Checklists dismiss pending row")
                          : showUndoPatch
                            ? tr("Checklists undo row changes")
                            : tr("Checklists remove")
                      }
                      title={
                        showDismissPending
                          ? tr("Checklists dismiss pending row")
                          : showUndoPatch
                            ? tr("Checklists undo row changes")
                            : tr("Checklists remove")
                      }
                      style={
                        showDismissPending
                          ? { color: "#6b7280" }
                          : showUndoPatch
                            ? undefined
                            : { color: "#dc2626" }
                      }
                      disabled={checklistQuery.isLoading}
                    >
                      {showDismissPending ? (
                        <RiCloseCircleFill size={22} />
                      ) : showUndoPatch ? (
                        <CgUndo size={24} className="text-blue-500" />
                      ) : (
                        <RiDeleteBin2Fill size={20} />
                      )}
                    </IconButton>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      {!readOnly && Number.isFinite(typeNum) ? (
        <div className="mt-2 relative" ref={templatePickerAnchorRef}>
          <OutlineButton type="button" onClick={() => setTemplatePickerOpen((o) => !o)}>
            {tr("Checklists add existing")}
          </OutlineButton>
          <ChecklistExistingPicker
            open={templatePickerOpen}
            onOpenChange={setTemplatePickerOpen}
            anchorRef={templatePickerAnchorRef}
            appointmentTypeId={typeNum}
            subAppointmentTypeId={defaultNum}
            variant="appointment"
            onConfirm={handleConfirmTemplatesFromPicker}
          />
        </div>
      ) : null}
    </div>
  );
});

AppointmentModalChecklistsSection.displayName = "AppointmentModalChecklistsSection";
